import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { SERVICES, DOCTORS } from "@/data/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AppointmentForm = ({ preselectedService = "", preselectedDoctor = "", compact = false }) => {
  const [form, setForm] = useState({
    name: "", phone: "", service: preselectedService, doctor: preselectedDoctor, preferred_time: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Будь ласка, заповніть ім'я та телефон");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/appointments`, form);
      setSubmitted(true);
    } catch {
      setError("Помилка при відправці. Спробуйте ще раз.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
        data-testid="appointment-success"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <CheckCircle className="w-16 h-16 text-[#C03A2B] mx-auto mb-4" strokeWidth={1.5} />
        </motion.div>
        <h3 className="text-2xl font-serif mb-2">Дякуємо за звернення!</h3>
        <p className="text-[#8C8C8C] font-sans">Ми зв'яжемося з вами протягом 30 хвилин</p>
        <button
          onClick={() => { setSubmitted(false); setForm({ name: "", phone: "", service: "", doctor: "", preferred_time: "", message: "" }); }}
          className="mt-6 text-[#C03A2B] hover:text-[#8B1F1F] font-sans font-medium transition-colors"
          data-testid="appointment-reset-btn"
        >
          Записати ще одного пацієнта
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-5 ${compact ? "" : "max-w-xl mx-auto"}`} data-testid="appointment-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold mb-2">
            Ім'я та прізвище *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-white border border-[#FAF0EE] rounded-xl px-4 py-3 text-[#1C1C1E] font-sans focus:outline-none focus:ring-2 focus:ring-[#C03A2B]/20 focus:border-[#C03A2B] transition-all"
            placeholder="Ваше ім'я"
            data-testid="appointment-name-input"
          />
        </div>
        <div>
          <label className="block text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold mb-2">
            Телефон *
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-white border border-[#FAF0EE] rounded-xl px-4 py-3 text-[#1C1C1E] font-sans focus:outline-none focus:ring-2 focus:ring-[#C03A2B]/20 focus:border-[#C03A2B] transition-all"
            placeholder="+38 (0__) ___ __ __"
            data-testid="appointment-phone-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold mb-2">Послуга</label>
          <select
            value={form.service}
            onChange={(e) => setForm({ ...form, service: e.target.value })}
            className="w-full bg-white border border-[#FAF0EE] rounded-xl px-4 py-3 text-[#1C1C1E] font-sans focus:outline-none focus:ring-2 focus:ring-[#C03A2B]/20 focus:border-[#C03A2B] transition-all appearance-none"
            data-testid="appointment-service-select"
          >
            <option value="">Оберіть послугу</option>
            {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold mb-2">Лікар</label>
          <select
            value={form.doctor}
            onChange={(e) => setForm({ ...form, doctor: e.target.value })}
            className="w-full bg-white border border-[#FAF0EE] rounded-xl px-4 py-3 text-[#1C1C1E] font-sans focus:outline-none focus:ring-2 focus:ring-[#C03A2B]/20 focus:border-[#C03A2B] transition-all appearance-none"
            data-testid="appointment-doctor-select"
          >
            <option value="">Будь-який лікар</option>
            {DOCTORS.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold mb-2">Зручний час</label>
        <input
          type="text"
          value={form.preferred_time}
          onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
          className="w-full bg-white border border-[#FAF0EE] rounded-xl px-4 py-3 text-[#1C1C1E] font-sans focus:outline-none focus:ring-2 focus:ring-[#C03A2B]/20 focus:border-[#C03A2B] transition-all"
          placeholder="Наприклад: будь-який ранок, вівторок після 14:00"
          data-testid="appointment-time-input"
        />
      </div>
      <AnimatePresence>
        {error && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-red-500 text-sm font-sans">
            {error}
          </motion.p>
        )}
      </AnimatePresence>
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-[#C03A2B] text-white font-sans px-8 py-4 rounded-full hover:bg-[#8B1F1F] transition-colors duration-300 font-medium text-lg flex items-center justify-center gap-2 disabled:opacity-70"
        data-testid="appointment-submit-btn"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        {loading ? "Відправляємо..." : "Записатись на прийом"}
      </button>
    </form>
  );
};
